import React, {useState} from 'react'
import {Box, Dialog, DialogContent, IconButton, Skeleton, Stack, ToggleButton, ToggleButtonGroup, Typography} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import {Cell, Pie, PieChart, ResponsiveContainer} from 'recharts'
import {useTranslation} from 'react-i18next'
import DashboardCard from '@containers/dashboard/ui/DashboardCard'
import ActivityBars from '@containers/dashboard/activity/ActivityBars'
import DonutCenter from '@containers/dashboard/activity/DonutCenter'
import ListDeviceActivity from '@containers/device/containers/deviceActivity/listDeviceActivity/ListDeviceActivity'
import {
	STATUSES_DEVICE_ACTIVITY,
	STATUSES_DEVICE_ACTIVITY_TOTAL,
} from '@containers/device/containers/deviceActivity/constants'
import {
	AllDeviceActivity,
	IDataDeviceActivity,
	StatusDeviceActivity,
} from '@containers/device/containers/deviceActivity/types'

interface IProps {
	data?: IDataDeviceActivity
}

// Both lists go in the same order, so a total key maps to its status by index.
const statusByTotalKey = (key: keyof AllDeviceActivity): StatusDeviceActivity =>
	STATUSES_DEVICE_ACTIVITY[STATUSES_DEVICE_ACTIVITY_TOTAL.findIndex((item) => item.value === key)].value

const ActivityCard = ({data}: IProps) => {
	const {t} = useTranslation()
	const [chart, setChart] = useState<'donut' | 'bars'>('donut')
	const [status, setStatus] = useState<StatusDeviceActivity | null>(null)
	const [hovered, setHovered] = useState<keyof AllDeviceActivity | null>(null)

	const rows = STATUSES_DEVICE_ACTIVITY_TOTAL.map((item) => ({
		...item,
		count: data?.total?.[item.value] ?? 0,
	}))
	const total = rows.reduce((sum, item) => sum + item.count, 0)
	const onlinePercent = total ? Math.round((data?.total?.totalOnline ?? 0) / total * 100) : 0

	const select = (key: keyof AllDeviceActivity) => setStatus(statusByTotalKey(key))
	const pieRows = rows.filter((item) => item.count > 0)
	const hoveredRow = pieRows.find((item) => item.value === hovered) ?? null

	return (
		<DashboardCard
			title={<>
				{t('dashboardPage.activity')}
				<Typography component={'span'} variant={'body2'} color={'text.secondary'} ml={1}>
					{t('dashboardPage.total', {value: total.toLocaleString('uk-UA')})}
				</Typography>
			</>}
			action={<ToggleButtonGroup
				exclusive
				size={'small'}
				value={chart}
				onChange={(_, value) => value && setChart(value)}
			>
				<ToggleButton value={'donut'}>{t('dashboardPage.donut')}</ToggleButton>
				<ToggleButton value={'bars'}>{t('dashboardPage.bars')}</ToggleButton>
			</ToggleButtonGroup>}
		>
			<Stack direction={{xs: 'column', sm: 'row'}} gap={2} alignItems={'center'}>
				{!data ? <Skeleton variant={'circular'} width={184} height={184} sx={{flexShrink: 0, m: '8px'}}/> : chart === 'donut' ? <Box
					sx={{
						position: 'relative', width: 200, height: 200, flexShrink: 0,
						// Sectors keep their element and scale from the ring centre, so hover animates both ways.
						'& .sector': {transition: 'transform .25s ease', transformBox: 'view-box', transformOrigin: '50% 50%'},
						'& .sector.hovered': {transform: 'scale(1.06)'},
					}}
				>
					<ResponsiveContainer width={'100%'} height={'100%'}>
						<PieChart>
							<Pie
								data={pieRows.map((item) => ({name: t(item.label), count: item.count}))}
								dataKey={'count'}
								innerRadius={62}
								outerRadius={90}
								paddingAngle={1}
								cursor={'pointer'}
								startAngle={90}
								endAngle={-270}
								animationDuration={800}
								onMouseEnter={(_, index) => setHovered(pieRows[index].value)}
								onMouseLeave={() => setHovered(null)}
								onClick={(_, index) => select(pieRows[index].value)}
							>
								{pieRows.map((item) => (
									<Cell
										key={item.value}
										fill={item.color}
										className={hovered === item.value ? 'sector hovered' : 'sector'}
									/>
								))}
							</Pie>
						</PieChart>
					</ResponsiveContainer>
					<DonutCenter
						value={hoveredRow ? hoveredRow.count : onlinePercent}
						suffix={hoveredRow ? '' : '%'}
						label={hoveredRow ? t(`dashboardPage.short.${hoveredRow.value}`) : t('dashboardPage.online')}
					/>
				</Box> : <Box sx={{flex: 1, width: '100%'}}>
					<ActivityBars rows={rows} onSelect={select}/>
				</Box>}

				<Box
					sx={{
						flex: chart === 'donut' ? 1 : '0 0 260px',
						display: 'grid',
						gridTemplateColumns: {xs: '1fr', lg: chart === 'donut' ? '1fr 1fr' : '1fr'},
						gap: '4px 16px',
						width: '100%',
					}}
				>
					{rows.map((item) => (
						<Stack
							key={item.value}
							direction={'row'}
							alignItems={'center'}
							gap={1}
							onClick={() => select(item.value)}
							onMouseEnter={() => setHovered(item.value)}
							onMouseLeave={() => setHovered(null)}
							sx={{
								cursor: 'pointer', borderRadius: '6px', px: .5, py: .25,
								bgcolor: hovered === item.value ? 'action.hover' : undefined,
							}}
						>
							<Box sx={{width: 10, height: 10, borderRadius: '50%', bgcolor: item.color, flexShrink: 0}}/>
							<Typography variant={'body2'} sx={{flex: 1}} noWrap>{t(item.label)}</Typography>
							{data
								? <Typography variant={'body2'} fontWeight={600}>{item.count.toLocaleString('uk-UA')}</Typography>
								: <Skeleton width={28}/>}
						</Stack>
					))}
				</Box>
			</Stack>

			<Typography variant={'caption'} color={'text.secondary'} display={'block'} mt={1}>
				{t('dashboardPage.activityHint')}
			</Typography>

			<Dialog open={!!status} onClose={() => setStatus(null)} fullScreen>
				<Stack direction={'row'} justifyContent={'flex-end'} p={1}>
					<IconButton onClick={() => setStatus(null)} aria-label={'close'}><CloseIcon/></IconButton>
				</Stack>
				<DialogContent sx={{pt: 0}}>
					{status && <ListDeviceActivity key={status} initialStatus={status}/>}
				</DialogContent>
			</Dialog>
		</DashboardCard>
	)
}

export default ActivityCard
